import React from 'react';
import type { RichContent } from '../../types';
import { isValidImageUrl } from '../../utils/fileValidation';
import { isSafeUrl } from '../../utils/urlSafety';

interface OrderCardsMessageProps {
  content: RichContent;
  onButtonClick?: (value: string) => void;
}

const OrderCardsMessage = React.memo(function OrderCardsMessage({
  content,
  onButtonClick,
}: OrderCardsMessageProps) {
  const orders = Array.isArray(content.orders) ? content.orders : [];
  if (orders.length === 0) return null;
  const currentOrder = orders[0];
  const historyOrders = orders.slice(1);
  const historyCount = historyOrders.length;
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [historyPage, setHistoryPage] = React.useState(0);
  const pageSize = 3;
  const pageCount = historyCount > 0 ? Math.ceil(historyCount / pageSize) : 0;
  const canPrev = historyPage > 0;
  const canNext = historyPage < pageCount - 1;

  React.useEffect(() => {
    setHistoryPage(0);
  }, [historyCount]);

  const pagedHistory = historyOpen
    ? historyOrders.slice(historyPage * pageSize, historyPage * pageSize + pageSize)
    : [];

  const renderOrderCard = (order: (typeof orders)[number], key: string) => {
    const lineItems = Array.isArray(order.lineItems) ? order.lineItems : [];
    return (
      <div key={key} className="rw-order-card">
        <div className="rw-order-header">
          <div>
            <div className="rw-order-title">{order.name}</div>
            {order.amount && <div className="rw-order-amount">{order.amount}</div>}
          </div>
          {order.status && (
            <span
              className={`rw-order-status ${order.status.toLowerCase() === 'delivered' ? 'is-delivered' : ''}`}
            >
              {order.status}
            </span>
          )}
        </div>

        {lineItems.length > 0 && (
          <div className="rw-order-items">
            {lineItems.map((item, idx) => (
              <div key={`${key}-item-${idx}`} className="rw-order-item">
                <div className="rw-order-item-media">
                  {item.image && isValidImageUrl(item.image) ? (
                    <img src={item.image} alt={item.title} loading="lazy" decoding="async" />
                  ) : (
                    <div className="rw-order-item-placeholder" />
                  )}
                </div>
                <div className="rw-order-item-body">
                  <div className="rw-order-item-title">{item.title}</div>
                  <div className="rw-order-item-meta">
                    {item.price && <span>{item.price}</span>}
                    {item.quantity !== undefined && item.quantity !== null && (
                      <span>x{item.quantity}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {order.actions && order.actions.length > 0 && (
          <div className="rw-order-actions">
            {order.actions.map((action, idx) => (
              <button
                key={`${key}-action-${idx}`}
                type="button"
                className="rw-order-action"
                onClick={() => {
                  if (action.href && isSafeUrl(action.href) && typeof window !== 'undefined') {
                    const target = action.target || '_blank';
                    if (target === '_self') {
                      try {
                        window.open(action.href, '_self');
                      } catch {
                        try {
                          window.location.assign(action.href);
                        } catch {
                          // Ignore if navigation is blocked.
                        }
                      }
                    } else {
                      window.open(action.href, target, 'noopener,noreferrer');
                    }
                    return;
                  }
                  if (action.value) onButtonClick?.(action.value);
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="rw-order-cards" role="region" aria-label="Order details">
      {currentOrder &&
        renderOrderCard(currentOrder, currentOrder.id || currentOrder.name || 'order-current')}

      {historyCount > 0 && (
        <div className="rw-order-history">
          <button
            type="button"
            className="rw-order-history-toggle"
            onClick={() => {
              setHistoryOpen(prev => {
                const next = !prev;
                if (next) setHistoryPage(0);
                return next;
              });
            }}
          >
            {historyOpen ? 'Hide order history' : 'View order history'}
          </button>

          {historyOpen && (
            <>
              <div className="rw-order-section">Order History</div>
              <div className="rw-order-history-list">
                {pagedHistory.map((order, index) =>
                  renderOrderCard(
                    order,
                    order.id || order.name || `history-${historyPage}-${index}`
                  )
                )}
              </div>
              {pageCount > 1 && (
                <div className="rw-order-history-controls">
                  <button
                    type="button"
                    className="rw-order-history-button"
                    onClick={() => setHistoryPage(page => Math.max(0, page - 1))}
                    disabled={!canPrev}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="rw-order-history-button"
                    onClick={() => setHistoryPage(page => Math.min(pageCount - 1, page + 1))}
                    disabled={!canNext}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
});

export default OrderCardsMessage;
